import React, { useEffect, useState } from 'react'
import {
    Card,
    CardContent,
    Typography,
    Theme,
    makeStyles,
    TextField,
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    Tooltip,
    useTheme,
    ListItemText,
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { createInspection } from '../../../../../../store'
import {
    ChatMessageContent,
    InspectionResponse,
    InspectionTypeResponse,
} from '../../../types'
import { useLoriState } from '../../../../../../contexts/LoriContext'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { CreateInspectionAxiosReq } from '../../../../../../store'
import {
    useAppDispatch,
    useAreaStatusTag,
    _useFinderSelection,
    useRootInfrastructure,
    useUser,
} from '../../../../../../hooks'
import {
    FinderLocationSelection,
    FinderSelectionMode,
} from '../../../../../../store'
import {
    getLineageUnits,
    IdBoolMap,
    Inspection,
    Unit,
    User,
} from '../../../../../../models'
import { toYYYYMMDD } from '../../../../../../helpers'
import { AssignInspection } from './AssignInspection'

// Define the interface for the selection state that will be passed to and from AssignInspection
export interface SelectedState {
    folders: Record<number, boolean>
    units: Record<number, boolean>
    areas: Record<number, boolean>
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderLeft: '4px solid',
        borderBottom: '4px solid',
        borderRight: '1px solid',
        borderTop: '1px solid',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(1),
    },
    header: {
        display: 'flex',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginBottom: theme.spacing(2),
    },
    title: {
        fontWeight: 700,
        fontSize: '1.1rem',
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
    submitButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    formControl: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    datePickers: {
        display: 'flex',
        gap: theme.spacing(2),
    },
    datePicker: {
        flex: 1,
    },
    unitSelectorHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    unitSelectorContent: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginTop: theme.spacing(2),
        borderTop: `2px solid ${theme.palette.primary.main}`,
    },
    infoIcon: {
        marginLeft: theme.spacing(1),
        fontSize: '1.2rem',
        color: theme.palette.text.secondary,
    },
    assignedToContainer: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        width: '100%',
    },
    shareWithTenantRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
}))

interface Props {
    message: ChatMessageContent
}

export const CreateInspectionCard: React.FC<Props> = ({ message }) => {
    if (!message.function_call_response) return null

    const theme = useTheme()
    const classes = useStyles(theme)
    const dispatch = useAppDispatch()
    const { handleSendMessage } = useLoriState()
    const { userList } = useUser()
    const { tree } = useRootInfrastructure(true)
    const finderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.RootSelection,
        selectionMode: FinderSelectionMode.Recursive,
        useCase: 'inspection',
    })

    const inspectionResponse = message.function_call_response as InspectionResponse
    const inspectionTypes: InspectionTypeResponse[] =
        inspectionResponse.inspectionTypes || []

    const [inspectionName, setInspectionName] = useState(
        inspectionResponse?.inspectionName || '',
    )
    const [inspectionType, setInspectionType] = useState<string>('')
    const [inspectionStartDate, setInspectionStartDate] = useState<Date | null>(
        inspectionResponse?.inspectionStartDate
            ? new Date(inspectionResponse.inspectionStartDate)
            : new Date(),
    )
    const [inspectionEndDate, setInspectionEndDate] = useState<Date | null>(
        inspectionResponse?.inspectionEndDate
            ? new Date(inspectionResponse.inspectionEndDate)
            : new Date(new Date().setDate(new Date().getDate() + 7)),
    )
    const [assignedTo, setAssignedTo] = useState<number>(-1)
    const [shareWithTenant, setShareWithTenant] = useState(false)

    // Single source of truth for the inspection selection
    const [
        inspectionSelection,
        setInspectionSelection,
    ] = useState<SelectedState>({
        folders: {},
        units: {},
        areas: {},
    })

    const [inspection, setInspection] = useState<Inspection | null>(null)
    const { areaStatusTagMap } = useAreaStatusTag({
        getListForSchedule: inspection?.schedule?.id,
    })

    // This callback will be passed to AssignInspection to update our state
    const handleSelectionChange = (newSelection: SelectedState) => {
        setInspectionSelection(newSelection)
    }

    const buildAreaStateForUnit = React.useCallback(
        (unit: Unit, areaState: IdBoolMap) => {
            const mutableAreaState: IdBoolMap = { ...areaState }

            if (unit.areas) {
                unit.areas.forEach((area) => {
                    if (
                        !areaStatusTagMap ||
                        areaStatusTagMap[area.id] === undefined
                    ) {
                        mutableAreaState[area.id] = true
                    } else {
                        mutableAreaState[area.id] = areaStatusTagMap[
                            area.id
                        ]!.area_status_config.should_service
                    }
                })
            }

            return mutableAreaState
        },
        [areaStatusTagMap],
    )

    // Process finder selection to update the tree selection
    useEffect(() => {
        // Don't update if there's no tree
        if (!tree || !tree.root) return

        // Create a new selection state based on finder selection
        const newSelection: SelectedState = {
            folders: { ...inspectionSelection.folders },
            units: { ...inspectionSelection.units },
            areas: { ...inspectionSelection.areas },
        }

        let hasChanges = false
        const handledUnits: Record<number, boolean> = {}

        // Process selected folders
        Object.keys(finderSelection.selection.folder).forEach((key) => {
            if (key === 'length') return

            const folder = finderSelection.selection.folder[parseInt(key)]
            if (!folder) return

            // Add folder to selection
            if (!newSelection.folders[folder.id]) {
                newSelection.folders[folder.id] = true
                hasChanges = true
            }

            // Add all units from this folder
            const folderUnits = getLineageUnits(folder)
            folderUnits.forEach((unit) => {
                if (handledUnits[unit.id]) return

                handledUnits[unit.id] = true

                // Add unit to selection
                if (!newSelection.units[unit.id]) {
                    newSelection.units[unit.id] = true
                    hasChanges = true
                }

                // Add areas for this unit
                if (unit.areas) {
                    unit.areas.forEach((area) => {
                        if (!newSelection.areas[area.id]) {
                            newSelection.areas[area.id] = true
                            hasChanges = true
                        }
                    })
                }
            })
        })

        // Process individually selected units
        Object.keys(finderSelection.selection.unit).forEach((key) => {
            if (key === 'length') return

            const unit = finderSelection.selection.unit[parseInt(key)]
            if (!unit || handledUnits[unit.id]) return

            handledUnits[unit.id] = true

            // Add unit to selection
            if (!newSelection.units[unit.id]) {
                newSelection.units[unit.id] = true
                hasChanges = true
            }

            // Add areas for this unit
            if (unit.areas) {
                unit.areas.forEach((area) => {
                    if (!newSelection.areas[area.id]) {
                        newSelection.areas[area.id] = true
                        hasChanges = true
                    }
                })
            }
        })

        // Only update if there were changes
        if (hasChanges) {
            setInspectionSelection(newSelection)
        }
    }, [finderSelection.selection, tree])

    const handleCreate = () => {
        // Extract area IDs from the selection
        const areaList = Object.entries(inspectionSelection.areas)
            .filter(([_, isSelected]) => isSelected)
            .map(([areaId]) => Number(areaId))

        const req: CreateInspectionAxiosReq = {
            body: {
                name: inspectionName,
                // Date-only calendar days (matches project schedule write contract).
                start_date: toYYYYMMDD(inspectionStartDate!),
                end_date: toYYYYMMDD(inspectionEndDate!),
                inspection_type: Number(inspectionType),
                areas: areaList,
                assign_to: assignedTo !== -1 ? assignedTo : undefined,
                share_with_tenant: shareWithTenant,
            },
        }

        // Create the inspection
        const newInspection = dispatch(createInspection(req))
        newInspection.then((response) => {
            setInspection(response.data)
        })
    }

    const handleSubmit = () => {
        // Validate form
        if (!inspectionName.trim()) {
            alert('Please enter an inspection name')
            return
        }

        if (!inspectionType) {
            alert('Please select an inspection type')
            return
        }

        if (!inspectionStartDate || !inspectionEndDate) {
            alert('Please select both start and end dates')
            return
        }

        if (inspectionEndDate < inspectionStartDate) {
            alert('End date cannot be before start date')
            return
        }

        // Make sure at least one area is selected
        const areaCount = Object.values(inspectionSelection.areas).filter(
            Boolean,
        ).length
        if (areaCount === 0) {
            alert('Please select at least one area for inspection')
            return
        }

        // Create the inspection
        handleCreate()

        // Create a message with all the inspection information
        const inspectionInfo = `An inspection with the following properties was created:
        - Name: ${inspectionName}
        - Type: ${inspectionType}
        - Start Date: ${inspectionStartDate?.toLocaleDateString()}
        - End Date: ${inspectionEndDate?.toLocaleDateString()}
        - Assigned To: ${
            assignedTo !== -1
                ? userList.find((user) => user.id === assignedTo)?.name
                : 'My Team'
        }
        - Selected Areas: ${areaCount}
        - Share with tenant: ${shareWithTenant ? 'Yes' : 'No'}`

        handleSendMessage(inspectionInfo, 'system')
    }

    return (
        <Card
            className={classes.root}
            style={{ borderColor: theme.palette.primary.main }}
        >
            <CardContent className={classes.content}>
                <div className={classes.header}>
                    <Typography className={classes.title}>
                        Create New Inspection
                    </Typography>
                </div>

                <TextField
                    className={classes.formField}
                    label="Inspection Name"
                    variant="outlined"
                    fullWidth
                    value={inspectionName}
                    onChange={(e) => setInspectionName(e.target.value)}
                />

                <FormControl variant="outlined" className={classes.formControl}>
                    <Autocomplete<InspectionTypeResponse>
                        options={inspectionTypes}
                        getOptionLabel={(option: InspectionTypeResponse) =>
                            option.name
                        }
                        value={
                            inspectionTypes.find(
                                (type) => type.id === Number(inspectionType),
                            ) || null
                        }
                        onChange={(
                            _: any,
                            newValue: InspectionTypeResponse | null,
                        ) => setInspectionType(newValue?.id.toString() || '')}
                        renderInput={(params: any) => (
                            <TextField
                                {...params}
                                label="Inspection Type"
                                variant="outlined"
                                placeholder="Search inspection types..."
                            />
                        )}
                        renderOption={(option: InspectionTypeResponse) => (
                            <MenuItem
                                key={option.id}
                                disabled={!option.has_inventory_configs}
                                style={
                                    !option.has_inventory_configs
                                        ? { opacity: 0.5 }
                                        : {}
                                }
                            >
                                <ListItemText primary={option.name} />
                            </MenuItem>
                        )}
                    />
                </FormControl>

                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <div className={classes.datePickers}>
                        <DatePicker
                            className={classes.datePicker}
                            label="Start Date"
                            value={inspectionStartDate}
                            onChange={setInspectionStartDate}
                            format="MM/dd/yyyy"
                            fullWidth
                            inputVariant="outlined"
                        />
                        <DatePicker
                            className={classes.datePicker}
                            label="End Date"
                            value={inspectionEndDate}
                            onChange={setInspectionEndDate}
                            format="MM/dd/yyyy"
                            fullWidth
                            inputVariant="outlined"
                            minDate={inspectionStartDate || undefined}
                        />
                    </div>
                </MuiPickersUtilsProvider>

                <div className={classes.shareWithTenantRow}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={shareWithTenant}
                                color="primary"
                                onChange={() =>
                                    setShareWithTenant(!shareWithTenant)
                                }
                            />
                        }
                        labelPlacement="start"
                        label="Share with tenant"
                    />
                    <Tooltip title="Tenants on matching units and areas can see upcoming dates and completed results in the tenant portal">
                        <InfoIcon
                            className={classes.infoIcon}
                            fontSize="small"
                        />
                    </Tooltip>
                </div>

                <div className={classes.unitSelectorContent}>
                    {tree && tree.root && (
                        <AssignInspection
                            root={tree.root}
                            onSelectionChange={handleSelectionChange}
                            initialSelected={inspectionSelection}
                        />
                    )}
                </div>

                <FormControl
                    variant="outlined"
                    className={classes.assignedToContainer}
                >
                    <InputLabel>Assigned To</InputLabel>
                    <Select
                        value={assignedTo}
                        onChange={(e) =>
                            setAssignedTo(e.target.value as number)
                        }
                        label="Assigned To"
                    >
                        <MenuItem value={-1}>My Team</MenuItem>
                        {userList.map((user: User) => (
                            <MenuItem key={user.id} value={user.id}>
                                {user.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <div className={classes.buttonContainer}>
                    <Button
                        variant="contained"
                        className={classes.submitButton}
                        onClick={handleSubmit}
                        disabled={
                            !inspectionName ||
                            !inspectionType ||
                            !inspectionStartDate ||
                            !inspectionEndDate ||
                            Object.keys(inspectionSelection.areas).length === 0
                        }
                    >
                        Create Inspection
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}
